import { useState, useCallback } from "react";
import { deleteConversation as deleteConversationService } from "../../../../../services/conversations";
import { useToast } from "../../../../../components/toast";

const DEFAULT_ERROR_MESSAGE = "Failed to delete conversation. Please try again.";

/**
 * Parses API response when HTTP status is 200 but body may contain statusCode 4xx.
 * For 404/500 the request throws and we handle in catch with a generic message.
 */
const parseDeleteResponse = (responseData) => {
  const statusCode = responseData?.status_code ?? responseData?.statusCode ?? 200;
  const msgCode = responseData?.msg_code ?? responseData?.msgCode ?? [];

  if (statusCode >= 200 && statusCode < 300) {
    return { success: true };
  }

  const errorMessage =
    msgCode.length > 0
      ? msgCode[0].description ?? msgCode[0].code ?? DEFAULT_ERROR_MESSAGE
      : DEFAULT_ERROR_MESSAGE;

  const error = new Error(errorMessage);
  error.response = { data: responseData, status: statusCode };
  throw error;
};

/**
 * Hook that deletes a conversation, parses 200-with-body-error responses,
 * updates context (clear selection, refresh list), and shows toast.
 * Expects deps from useHomeChatConversations and useHomeChatAgent.
 */
export const useDeleteConversation = ({
  selectConversation,
  fetchConversations,
  selectedConversationId,
  selectedAgentId,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  const deleteConversation = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      setIsDeleting(true);
      try {
        const responseData = await deleteConversationService(conversationId);
        parseDeleteResponse(responseData);

        if (selectedConversationId === conversationId) {
          selectConversation(null);
        }
        await fetchConversations(selectedAgentId, null);
        toast.success({ message: "The conversation was successfully deleted." });
      } catch (error) {
        const message =
          error?.response?.data?.message ?? error?.message ?? DEFAULT_ERROR_MESSAGE;
        toast.error({ message });
        throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    [
      selectConversation,
      fetchConversations,
      selectedConversationId,
      selectedAgentId,
      toast,
    ]
  );

  return { deleteConversation, isDeleting };
};
